import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Button, Input, InputGroup, InputGroupText } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash';
import { musicServicesByMusicAPIID } from 'fym-shared/src/music-services/allMusicServices';
import { TypedMemo } from '../../common/types';
import { PresaveReleaseUrlItem } from './usePresaveReleaseUrls';

interface MarkReleasedUrlRowProps {
  item: PresaveReleaseUrlItem;
  disabled?: boolean;
  onChange: (id: string, url: string) => void;
  onRemove: (id: string) => void;
}

/** One music service's link to the release, as the artist may still fix it. */
const MarkReleasedUrlRowComponent: React.FC<MarkReleasedUrlRowProps> = ({ item, disabled, onChange, onRemove }) => {
  const intl = useIntl();

  const label = useMemo(() => {
    if (item.source === undefined) {
      return intl.formatMessage({ id: 'app.sml.presave.release.unknown_service', defaultMessage: 'Other' });
    }
    return musicServicesByMusicAPIID[item.source]?.name ?? item.source;
  }, [intl, item.source]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(item.id, event.target.value);
    },
    [item.id, onChange]
  );

  const handleRemove = useCallback(() => {
    onRemove(item.id);
  }, [item.id, onRemove]);

  return (
    <InputGroup className="mb-2">
      <InputGroupText>{label}</InputGroupText>
      <Input type="text" value={item.url} disabled={disabled} onChange={handleChange} />
      <Button
        color="link"
        disabled={disabled}
        onClick={handleRemove}
        aria-label={intl.formatMessage({ id: 'app.forms.buttons.delete', defaultMessage: 'Delete' })}
      >
        <FontAwesomeIcon icon={faTrash} />
      </Button>
    </InputGroup>
  );
};

export const MarkReleasedUrlRow = TypedMemo(MarkReleasedUrlRowComponent);
